import { useState } from 'react';
import { TOOLBOX_STAGES, useToolbox } from '../context/ToolboxContext';
import { useWorkflows } from '../context/WorkflowsContext';
import { ToolboxStage } from './ToolboxStage';

export function ToolboxTab() {
  const { toolbox, clearToolbox, filledStagesCount } = useToolbox();
  const { addWorkflow } = useWorkflows();
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [workflowName, setWorkflowName] = useState('');
  const [saveError, setSaveError] = useState('');

  const handleSave = () => {
    if (!workflowName.trim() && filledStagesCount === 0) {
      setSaveError('Add at least one tool and enter a workflow name.');
      return;
    }
    if (!workflowName.trim()) {
      setSaveError('Please enter a workflow name.');
      return;
    }
    if (filledStagesCount === 0) {
      setSaveError('Add at least one tool to your toolbox first.');
      return;
    }
    const created = addWorkflow(workflowName, toolbox);
    if (!created) {
      setSaveError('Something went wrong. Please try again.');
      return;
    }
    setSaveError('');
    setWorkflowName('');
    setIsSaveOpen(false);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-neutral-200 bg-neutral-50 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-neutral-800 font-heading">Your AI Toolbox</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {filledStagesCount} / {TOOLBOX_STAGES.length} Tools Added
            </p>
          </div>
          <div className="flex items-center gap-2">
            {filledStagesCount > 0 && (
              <>
                <button
                  onClick={() => setIsSaveOpen(true)}
                  className="btn-ghost text-primary-600 hover:text-primary-700 hover:bg-primary-50"
                >
                  Save Workflow
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Clear all tools from your toolbox? This can\'t be undone.')) {
                      clearToolbox();
                    }
                  }}
                  className="btn-ghost text-neutral-600 hover:text-red-600 hover:bg-red-50"
                >
                  Clear All
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stages - scrollable */}
      <div className={`flex-1 p-6 space-y-1 ${filledStagesCount === 0 ? 'overflow-y-hidden' : 'overflow-y-auto overscroll-contain'}`}>
        {filledStagesCount === 0 ? (
          <EmptyState />
        ) : (
          TOOLBOX_STAGES.map((stage, index) => (
            <ToolboxStage
              key={stage.id}
              id={stage.id}
              label={stage.label}
              icon={stage.icon}
              tool={toolbox[stage.id]}
              isLast={index === TOOLBOX_STAGES.length - 1}
            />
          ))
        )}
      </div>

      {isSaveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-card border border-neutral-200 shadow-modal w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-base font-semibold text-neutral-800 font-heading">Save Workflow</h4>
              <button
                onClick={() => {
                  setIsSaveOpen(false);
                  setSaveError('');
                }}
                className="text-neutral-400 hover:text-neutral-600"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-xs text-neutral-500 mb-4">
              Name this workflow so you can find it later in Workflows.
            </p>
            <input
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              placeholder="e.g. Intake + Documentation Stack"
              className="input-base"
            />
            {saveError && <p className="text-xs text-red-600 mt-2">{saveError}</p>}
            <div className="flex items-center justify-end gap-2 mt-4">
              <button
                onClick={() => setIsSaveOpen(false)}
                className="btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="btn-primary"
              >
                Save Workflow
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-6 py-12">
      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-neutral-800 mb-2 font-heading">Build Your Toolbox</h3>
      <p className="text-sm text-neutral-500 max-w-sm">
        Start a conversation with the AI assistant to get personalized tool recommendations, or browse all available tools below.
      </p>
    </div>
  );
}
